import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarUpload } from '../ui/avatar';
import { useAvatar } from '../../contexts/AvatarContext';
import { useSubscription } from '../../hooks/useSubscription';
import { 
  Mail, 
  Calendar, 
  Edit3, 
  Star,
  Crown
} from 'lucide-react';

const ProfileHeader = ({ 
  profile, 
  stats, 
  editing, 
  setEditing, 
  getDisplayName, 
  getInitials 
}) => {
  const { avatar } = useAvatar();
  const { subscription } = useSubscription();
  const [showAvatarUpload, setShowAvatarUpload] = useState(false);

  const handleAvatarClick = () => {
    setShowAvatarUpload(true);
  };

  const getSubscriptionDisplay = () => {
    if (!subscription) return { label: 'Free', variant: 'outline', icon: Star };
    
    const isPro = subscription.tier_name === 'pro';
    return {
      label: subscription.tier_display_name || 'Free',
      variant: isPro ? 'default' : 'outline',
      icon: isPro ? Crown : Star
    };
  };

  const subscriptionInfo = getSubscriptionDisplay();

  return (
    <>
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-600 to-indigo-600 opacity-10"></div>
        <CardContent className="relative p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <Avatar
                size="2xl"
                src={avatar?.url}
                initials={avatar?.initials || getInitials()}
                color={avatar?.color}
                type={avatar?.type}
                onClick={handleAvatarClick}
                showUploadButton={true}
              />
            </div>
          
          {/* Profile Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {getDisplayName()}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4" />
                  {profile?.email}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Member since {stats.memberSince}
                  </Badge>
                  <Badge variant={subscriptionInfo.variant} className="flex items-center gap-1">
                    <subscriptionInfo.icon className="w-3 h-3" />
                    {subscriptionInfo.label}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(!editing)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Edit3 className="w-4 h-4" />
                  {editing ? 'Cancel' : 'Edit Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    
    {/* Avatar Upload Modal */}
    {showAvatarUpload && (
      <AvatarUpload
        currentAvatar={avatar}
        onClose={() => setShowAvatarUpload(false)}
        userId={profile?.id}
      />
    )}
  </>
  );
};

export default ProfileHeader;
